import { PropsWithChildren, useMemo, useState } from "react";

import { PlusOutlined } from "@aircall/icons";
import { Button, Divider, FormProps, Spacer, useColor } from "@aircall/tractor";
import { useTranslation } from "react-i18next";

import { useCreateNote } from "@/hooks/useCreateNote/useCreateNote";
import { SectionNotesForm } from "../SectionNotesForm";

import { NotesListProps } from "./NotesList.decl";
import { NotesListItem } from "./NotesListItem";

import { NoteAgentDetail } from "@/graphql/generated/graphql";

function byDateDesc(a: NoteAgentDetail, b: NoteAgentDetail) {
  const aDate = new Date(a.createdAt).getTime();
  const bDate = new Date(b.createdAt).getTime();

  return bDate - aDate;
}

function ListLayout({ children }: PropsWithChildren<unknown>) {
  return (
    <Spacer data-test="notes-list" gap="s" direction="vertical" w="100%">
      {children}
    </Spacer>
  );
}

export function NotesList({ notes }: NotesListProps) {
  const [displayButton, setDisplayButton] = useState(true);

  const { t } = useTranslation();
  const sorted = useMemo(() => [...notes].sort(byDateDesc), [notes]);
  const graphicDefaultColor = useColor("graphic-default");

  const handleOnClick = () => {
    setDisplayButton(false);
  };
  const handleOnCancel = () => {
    setDisplayButton(true);
  };

  const { handleOnSubmit, loading: isLoadingMutation } = useCreateNote();

  const handleOnSubmitEnhanced: FormProps["onSubmit"] = (e) => {
    handleOnSubmit(e);
    setDisplayButton(true);
  };

  return (
    <Spacer gap="s" direction="vertical" w="100%">
      <ListLayout>
        {sorted.map((note, index) => {
          if (index === 0) {
            return (
              <NotesListItem
                key={note.id}
                id={note.id}
                content={note.content}
                createdAt={note.createdAt}
                firstName={note.firstName}
                lastName={note.lastName}
                userId={note.userId}
                isLoadingMutation={isLoadingMutation}
              />
            );
          }

          return (
            <Spacer key={index} direction="vertical" gap="s" fluid>
              <Divider
                w="100%"
                borderTop={`1px solid ${graphicDefaultColor}`}
              />
              <NotesListItem
                key={note.id}
                id={note.id}
                content={note.content}
                createdAt={note.createdAt}
                firstName={note.firstName}
                lastName={note.lastName}
                userId={note.userId}
                isLoadingMutation={isLoadingMutation}
              />
            </Spacer>
          );
        })}
      </ListLayout>
      {displayButton && (
        <Button
          data-test="add_notes_button"
          onClick={handleOnClick}
          mode="ghost"
          size="xSmall"
        >
          <PlusOutlined />
          {t("add_note")}
        </Button>
      )}
      <SectionNotesForm
        show={!displayButton}
        onSubmit={handleOnSubmitEnhanced}
        onCancel={handleOnCancel}
        showSeparator
      />
    </Spacer>
  );
}
